import React from 'react';
import { AlertOctagon, RefreshCcw } from 'lucide-react';

interface State {
  error: Error | null;
}

/** Render-time error boundary so a crash in one workspace doesn't blank the
 *  whole app. Logs to console + shows a small recovery card. */
export class ErrorBoundary extends React.Component<React.PropsWithChildren<{ label?: string }>, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: React.ErrorInfo) {
    // eslint-disable-next-line no-console
    console.error('ErrorBoundary caught', this.props.label ?? '', error, info);
  }

  reset = () => this.setState({ error: null });

  override render() {
    if (this.state.error) {
      return (
        <div className="grid h-full place-items-center p-6">
          <div className="max-w-md rounded-lg border border-status-critical/40 bg-status-critical/10 p-5">
            <div className="flex items-center gap-2">
              <AlertOctagon className="h-5 w-5 text-status-critical" />
              <h2 className="text-sm font-bold text-status-critical">
                {this.props.label ?? 'Something broke'}
              </h2>
            </div>
            <pre className="mt-3 max-h-40 overflow-auto rounded bg-base/40 p-2 font-mono text-[10px] text-fg-secondary">
              {this.state.error.message}
            </pre>
            <button
              onClick={this.reset}
              className="mt-3 inline-flex items-center gap-1.5 rounded bg-accent px-3 py-1.5 text-xs font-semibold text-white hover:bg-accent-strong"
            >
              <RefreshCcw className="h-3.5 w-3.5" />
              Retry
            </button>
          </div>
        </div>
      );
    }
    return this.props.children;
  }
}
